import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Card, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, MenuItem, CircularProgress, alpha, Divider, Stack 
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import storage from 'src/utils/storageUtils';

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' }
];

const LANGUAGES = [
  { value: 'it', label: 'Italian (it)' },
  { value: 'en_US', label: 'English (US - en_US)' },
  { value: 'en_GB', label: 'English (UK - en_GB)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'gu', label: 'Gujarati (gu)' }
];

function WhatsAppTemplates({ clinicId }) {
  const theme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [title, setTitle] = useState('');
  const [metaCategory, setMetaCategory] = useState('MARKETING');
  const [language, setLanguage] = useState('it');
  const [text, setText] = useState('');
  const [button1, setButton1] = useState('');
  const [button2, setButton2] = useState('');

  // View Modal
  const [viewingTemplate, setViewingTemplate] = useState(null);

  useEffect(() => {
    if (clinicId) {
      fetchTemplates();
    }
  }, [clinicId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = storage.get('TOKEN');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/template/list?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch templates", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreFillItalian = () => {
    setTitle('metismeet_growth_automation_v1');
    setMetaCategory('MARKETING');
    setLanguage('it');
    setText(
      'Ciao {{1}},\n\nI flussi di lavoro manuali e i sistemi frammentati stanno rallentando le tue operazioni aziendali?\n\nNoi di MetisMeet realizziamo sistemi ERP personalizzati, automazioni basate sull\'intelligenza artificiale e soluzioni web scalabili che aiutano le imprese moderne a eliminare i colli di bottiglia operativi, snellire i processi e far crescere la propria attività in modo efficiente.\n\nTi piacerebbe scoprire come possiamo ottimizzare e automatizzare i tuoi processi aziendali?\n\nRispondi STOP per annullare l’iscrizione.'
    );
    setButton1('Scopri le soluzioni');
    setButton2('Non ora');
  };

  const handlePreFillEnglish = () => {
    setTitle('metismeet_growth_automation_v1');
    setMetaCategory('MARKETING');
    setLanguage('en_US');
    setText(
      'Hi {{1}},\n\nAre manual workflows and fragmented systems slowing down your business operations?\n\nAt MetisMeet, we build custom ERP systems, AI-powered automations, and scalable web solutions that help modern enterprises eliminate operational bottlenecks, streamline processes, and scale their businesses efficiently.\n\nWould you like to discover how we can optimize and automate your business processes?\n\nReply STOP to unsubscribe.'
    );
    setButton1('Explore Solutions');
    setButton2('Not Now');
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      const token = storage.get('TOKEN');

      const components = [
        {
          type: "BODY",
          text: text
        }
      ];

      if (button1.trim() || button2.trim()) {
        const buttons = [];
        if (button1.trim()) {
          buttons.push({ type: "QUICK_REPLY", text: button1.trim() });
        }
        if (button2.trim()) {
          buttons.push({ type: "QUICK_REPLY", text: button2.trim() });
        }
        components.push({
          type: "BUTTONS",
          buttons: buttons
        });
      }

      const payload = {
        title: title.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        text,
        type: 'message',
        metaCategory,
        language,
        clinic: clinicId,
        components
      };

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/template`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOpenModal(false);
      fetchTemplates();
      setTitle('');
      setText('');
      setButton1('');
      setButton2('');
    } catch (error) {
      console.error("Failed to create template", error);
      alert(error.response?.data?.message || 'Failed to submit template to Meta');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    try {
      setLoading(true);
      const token = storage.get('TOKEN');
      await axios.delete(
        `${process.env.REACT_APP_BASE_API_URL}/template/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTemplates();
    } catch (error) {
      console.error("Failed to delete template", error);
      alert("Failed to delete template");
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">WhatsApp Template Manager</Typography>
          <Typography variant="body2" color="textSecondary">Create, manage, and submit marketing & utility templates directly to Meta</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<SyncIcon />} onClick={fetchTemplates}>
            Sync from Meta
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenModal(true)}>
            New Template
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[1], border: `1px solid ${theme.palette.divider}` }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <TableRow>
                <TableCell><Typography fontWeight="bold">Template Name</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Category</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Language</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Status</Typography></TableCell>
                <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">No templates found. Click "New Template" to create one!</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell fontWeight="600">{template.title}</TableCell>
                    <TableCell>{template.metaCategory || template.category || 'MARKETING'}</TableCell>
                    <TableCell>
                      <Chip label={template.language || 'it'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={template.metaStatus || 'NOT_SUBMITTED'} 
                        color={getStatusColor(template.metaStatus)} 
                        size="small" 
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => setViewingTemplate(template)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(template.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* View Template Modal */}
      <Dialog open={Boolean(viewingTemplate)} onClose={() => setViewingTemplate(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>
          Template: {viewingTemplate?.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box mb={2} display="flex" gap={1}>
            <Chip label={`Category: ${viewingTemplate?.metaCategory || viewingTemplate?.category || 'MARKETING'}`} size="small" />
            <Chip label={`Language: ${viewingTemplate?.language || 'it'}`} size="small" />
            <Chip label={viewingTemplate?.metaStatus || 'NOT_SUBMITTED'} color={getStatusColor(viewingTemplate?.metaStatus)} size="small" />
          </Box>
          <Box bgcolor="#efeae2" p={2.5} borderRadius={3} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <Box bgcolor="#fff" p={2} borderRadius={2} boxShadow={1}>
              <Typography variant="body1" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word', fontSize: '15px' }}>
                {viewingTemplate?.text}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setViewingTemplate(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
          Create WhatsApp Template for Meta Review
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Quick Pre-fill Actions */}
          <Box mb={2} p={1.5} bgcolor={alpha(theme.palette.primary.main, 0.04)} borderRadius={2} border={`1px dashed ${theme.palette.primary.main}`}>
            <Typography variant="caption" fontWeight="bold" display="block" mb={1} color="primary">
              QUICK TEMPLATES (METISMEET MARKETING PRESETS):
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button 
                size="small" 
                variant="outlined" 
                startIcon={<AutoAwesomeIcon />}
                onClick={handlePreFillItalian}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Italian Marketing Preset
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                startIcon={<AutoAwesomeIcon />}
                onClick={handlePreFillEnglish}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                English Marketing Preset
              </Button>
            </Stack>
          </Box>

          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            {/* Form Area */}
            <Box flex={1} display="flex" flexDirection="column" gap={2.5}>
              <TextField 
                label="Template Name" 
                fullWidth 
                required 
                value={title}
                onChange={(e) => setTitle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                helperText="Lowercase letters, numbers, and underscores only (e.g., metismeet_growth_automation_v1)"
              />
              <Box display="flex" gap={2}>
                <TextField 
                  select 
                  label="Category" 
                  fullWidth 
                  value={metaCategory}
                  onChange={(e) => setMetaCategory(e.target.value)}
                >
                  {CATEGORIES.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
                <TextField 
                  select 
                  label="Language" 
                  fullWidth 
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  {LANGUAGES.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              </Box>
              <TextField 
                label="Message Body" 
                multiline 
                rows={6} 
                fullWidth 
                required 
                value={text}
                onChange={(e) => setText(e.target.value)}
                helperText="Use {{1}}, {{2}} for dynamic patient variables."
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold">Quick Reply Buttons (Optional)</Typography>
              <Box display="flex" gap={2}>
                <TextField 
                  label="Button 1 (e.g. Yes / Start)" 
                  fullWidth 
                  size="small"
                  value={button1}
                  onChange={(e) => setButton1(e.target.value)}
                />
                <TextField 
                  label="Button 2 (e.g. No / Opt-out)" 
                  fullWidth 
                  size="small"
                  value={button2}
                  onChange={(e) => setButton2(e.target.value)}
                />
              </Box>
            </Box>

            {/* Phone Chat Preview Area */}
            <Box 
              width={{ xs: '100%', md: '320px' }} 
              bgcolor="#efeae2" 
              p={2} 
              borderRadius={3} 
              sx={{ 
                border: '6px solid #2d3748',
                boxShadow: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '380px'
              }}
            >
              <Box>
                <Typography variant="caption" align="center" display="block" mb={1.5} bgcolor="rgba(255,255,255,0.85)" py={0.5} borderRadius={1} fontWeight="bold">
                  WhatsApp Preview ({language})
                </Typography>
                
                {/* Message Bubble */}
                <Box bgcolor="#fff" p={1.5} borderRadius={2} boxShadow={1}>
                  <Typography variant="body2" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word', fontSize: '13px', lineHeight: 1.5 }}>
                    {text || 'Type your message or select a preset to see the live WhatsApp preview...'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '10px' }}>
                    10:30 AM ✓✓
                  </Typography>
                </Box>

                {/* Quick Reply Buttons Preview */}
                {(button1 || button2) && (
                  <Stack spacing={1} mt={1}>
                    {button1 && (
                      <Box bgcolor="#fff" py={0.8} px={1} borderRadius={1.5} textAlign="center" boxShadow={1} sx={{ cursor: 'pointer', border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="primary" fontWeight="bold">
                          ↩ {button1}
                        </Typography>
                      </Box>
                    )}
                    {button2 && (
                      <Box bgcolor="#fff" py={0.8} px={1} borderRadius={1.5} textAlign="center" boxShadow={1} sx={{ cursor: 'pointer', border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          ↩ {button2}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>

              <Typography variant="caption" color="textSecondary" align="center" mt={2} sx={{ fontSize: '10px' }}>
                Meta evaluates template text and buttons within 24h.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setOpenModal(false)} color="inherit" disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" color="primary" disabled={saving || !title || !text}>
            {saving ? 'Submitting to Meta...' : 'Submit for Meta Approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WhatsAppTemplates;
